<template>
  <div class="dashboard-wrapper page-gutter page-gutter--both">

    <!-- Header -->
    <Section
      :title="detectionZone?.DetectionZoneName
                ? $t('zoneDetail.title', { zone: detectionZone.DetectionZoneName })
                : $t('zoneDetail.titlePlain')"
      :badge="openingHoursMode"
      :accent="zoneColor"
    >
      <template #head-right>
        <!-- Geometry chip — self-describing; the label text inside
             the chip is already the same as zoneGeometryLabel, so a
             tooltip repeating it would be pure noise. -->
        <span class="zone-geom-chip">
          <i v-if="zoneHasPolygon" class="bi bi-bounding-box-circles"></i>
          <i v-if="zoneHasTripwire" class="bi bi-arrow-left-right"></i>
          <span class="zone-geom-text">{{ zoneGeometryLabel }}</span>
        </span>
      </template>

      <!-- Filters -->
      <ControlHead>
        <duration-dropdown
          :dateRangeMode="dateRangeMode"
          :selectedDuration="duration"
          @updateDateRangeMode="changeDateRangeMode"
          @updateDuration="changeDuration"
          @clearDatePicker="updateClearDatePicker"
        />
        <date-picker
          :clear-date-picker="clearDatePicker"
          @updateStartDate="updateStartDate"
          @updateEndDate="updateEndDate"
          @updateDateRangeMode="changeDateRangeMode"
          @updateClearDatePicker="updateClearDatePicker"
        />
        <dropdown-opening-hours-mode
          :openingHoursMode="openingHoursMode"
          :disabledModes="disabledOpeningHourModes"
          @updateOpeningHoursMode="updateOpeningHoursMode"
        />

        <template #actions>
          <button class="toggle-popup" @click="toggleAllensInsights">
            <i class="bi bi-lightning-charge"></i> {{ $t('zoneDetail.allenInsights') }}
          </button>
        </template>
      </ControlHead>

      <!-- Zone identity 6.2: the zone changed type today — its figures are still those
           of the old type until the nightly run, so none are shown here. -->
      <!-- 6.7: settings saved today that this page's figures follow only tonight. -->
      <ChangeNotice v-if="!typeChange && openingHoursChanged" :compact="true" :text="NOTICES.settings.opening_hours" />
      <ChangeNotice v-if="!typeChange && thresholdChanged" :text="NOTICES.settings.thresholds" />
      <ChangeNotice
        v-if="typeChange"
        :text="NOTICES.zonePage"
        :detail="$t('zoneDetail.typeChanged', { to: typeNameOf(typeChange.to), from: typeNameOf(typeChange.from) })"
      />

      <template v-if="!typeChange">
      <div v-if="sameIdInOutSetting" class="same-id-note">
        <i class="bi bi-exclamation-triangle"></i>
        {{ $t('zoneDetail.sameId') }}
      </div>

      <!-- Per-zone weather response — data view: shown for every zone (incl. an honest
           "no clear weather link") whenever the store has a configured weather location. -->
      <ZoneWeatherCard
        v-if="weatherEnabled && detectionZone"
        :zone-name="detectionZone.DetectionZoneName"
        :zones="weatherZoneSignals"
        :use-crossings="useCrossingsLabel"
        :curve="zoneWeatherCurve"
        style="margin: 14px 0 2px;"
      />

      <!-- Checkout queue — length of the line in this checkout's waiting zone
           (from zone-occupancy overlap). Only for Checkout Waiting zones. -->
      <div v-if="checkoutQueue" class="checkout-queue-card">
        <div class="cq-head">
          <i class="bi bi-people"></i>
          <span>{{ $t('zoneDetail.queue.title') }}</span>
        </div>
        <p class="cq-body">
          <strong class="cq-verdict">{{ checkoutQueueVerdict }}</strong> {{ $t('zoneDetail.queue.body', { pct: pctText(Math.round(checkoutQueue.pct_time_ge2)), peak: checkoutQueue.peak_line }) }}
        </p>
      </div>

      <!-- Render charts ONLY when results are ready (restores old guard) -->
      <template v-if="results && results.visitors_duration_segmentation">
        <!-- Row 1 — polygon dwell triplet: hidden in tripwire-only mode
             (these panels rely on visit-duration data that doesn't exist
             for line-crossing events). -->
        <div v-if="!useTripwireMode" class="chart-grid chart-grid--3" style="margin-top: 12px;">
          <ChartPanel
            :title="$t('zoneDetail.panel.segmentation')"
            icon="bi-people"
            :height="500"
            :empty="!results?.chart_duration_segmentation"
            :accent="zoneColor"
          >
            <ChartVisitorSegmentation
              :results="results"
              :durations="results.chart_duration_segmentation"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
            />
          </ChartPanel>

          <ChartPanel
            :title="$t('zoneDetail.panel.totalTime')"
            icon="bi-clock-history"
            :height="500"
            :empty="!results?.total_time_by_visitor_duration_segmentation"
            :accent="zoneColor"
          >
            <template v-if="results?.visitors_duration_segmentation?.strategy !== 'insufficient_data'">
              <ChartTotalDurationSegmentation
                :durations="results.total_time_by_visitor_duration_segmentation"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
              />
            </template>
            <template v-else>
              <div class="empty-fallback">{{ $t('zoneDetail.panel.totalTimeEmpty') }}</div>
            </template>
          </ChartPanel>

          <ChartPanel
            :title="$t('zoneDetail.panel.occupation')"
            icon="bi-activity"
            :height="500"
            :empty="!results?.usage_percentage"
            :accent="zoneColor"
          >
            <ChartZoneOccupation
              :usage="results.usage_percentage"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
            />
          </ChartPanel>
        </div>

        <!-- Row 2 — Visits + Average Duration (polygon-derived). Hidden in
             tripwire-only mode; the Crossings panel below carries the
             equivalent over-time signal for those zones. -->
        <div v-if="!useTripwireMode" class="row2-grid" style="margin-top:16px;">
          <ChartPanel
            class="row2-item"
            :class="(countsGranularity === 'hourly' || avgDurGranularity === 'hourly') ? 'row2-span-6' : 'row2-span-3'"
            :title="$t('zoneDetail.panel.entries')"
            :subtitle="$t('zoneDetail.panel.entriesSub')"
            icon="bi-graph-up"
            :height="360"
            :note="countsMarkerCaption || ''"
            :empty="!countsChartData"
            :accent="zoneColor"
          >
            <div class="grid-panel-toolbar">
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateCountsGranularity" />
              <label v-if="weatherEnabled && countsGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('zoneDetail.weather.visitsTip')">
                <input type="checkbox" v-model="weatherOverlay" />
                <i class="bi bi-thermometer-half"></i> {{ $t('zoneDetail.weather.toggle') }}
              </label>
            </div>
            <ChartLineTotalCountsDetectionZone
              v-if="detectionZone && countsChartData"
              :chart-data="countsChartData"
              :zone-name="detectionZone?.DetectionZoneName"
              :timeSpan="durationText"
              :show-weather="weatherEnabled && weatherOverlay && countsGranularity === 'daily'"
              :weather-by-date="weatherDailyTemp"
              :markers="zoneMarkers"
            />
          </ChartPanel>

          <ChartPanel
            class="row2-item"
            :class="(countsGranularity === 'hourly' || avgDurGranularity === 'hourly') ? 'row2-span-6' : 'row2-span-3'"
            :title="$t('zoneDetail.panel.avgDuration')"
            :subtitle="$t('zoneDetail.panel.avgDurationSub')"
            icon="bi-stopwatch"
            :height="360"
            :note="avgDurMarkerCaption || ''"
            :empty="!avgDurChartData"
            :accent="zoneColor"
          >
            <div class="grid-panel-toolbar">
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateAvgDurGranularity" />
            </div>
            <ChartLineAverageDurationDetectionZone
              v-if="detectionZone && avgDurChartData"
              :chart-data="avgDurChartData"
              :zone-name="detectionZone?.DetectionZoneName"
              :timeSpan="durationText"
              :markers="zoneMarkers"
            />
          </ChartPanel>
        </div>

        <!-- Tripwire-only zone layout (daily granularity):
               Row 1: Crossings (2/3)            | Top Crossing Highlights (1/3)
               Row 2: Heatmap (1/2)              | Trends + Avg Comparison stacked (1/2)
             Hourly: Crossings spans full width, the other three split the
             second row in 1/3 each. -->
        <div
          v-if="useTripwireMode"
          class="tripwire-layout-grid"
          :class="{ 'tripwire-hourly': tripwireGranularity === 'hourly' }"
          style="margin-top:16px;"
        >
          <!-- Row 1, left (2/3): Crossings line chart -->
          <ChartPanel
            class="tw-area-crossings"
            :title="crossingsPanelTitle"
            icon="bi-arrow-left-right"
            :height="360"
            :note="tripwireMarkerCaption || ''"
            :empty="!tripwireChartData"
            :accent="zoneColor"
          >
            <div class="grid-panel-toolbar tripwire-toolbar">
              <div class="tripwire-totals">
                <span class="tw-pill tw-in">
                  <i class="bi bi-arrow-down-right"></i>
                  {{ tripwireInLabel }}: <b>{{ results?.tripwire_total_in || 0 }}</b>
                </span>
                <span class="tw-pill tw-out">
                  <i class="bi bi-arrow-up-right"></i>
                  {{ tripwireOutLabel }}: <b>{{ results?.tripwire_total_out || 0 }}</b>
                </span>
              </div>
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateTripwireGranularity" />
              <label v-if="weatherEnabled && tripwireGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('zoneDetail.weather.crossingsTip')">
                <input type="checkbox" v-model="weatherOverlay" />
                <i class="bi bi-thermometer-half"></i> {{ $t('zoneDetail.weather.toggle') }}
              </label>
            </div>
            <ChartLineTripwireCrossingsDetectionZone
              v-if="tripwireChartData"
              :chart-data="tripwireChartData"
              :timeSpan="durationText"
              :in-label="tripwireInLabel"
              :out-label="tripwireOutLabel"
              :show-weather="weatherEnabled && weatherOverlay && tripwireGranularity === 'daily'"
              :weather-by-date="weatherDailyTemp"
              :markers="zoneMarkers"
            />
          </ChartPanel>

          <!-- Row 1, right (1/3): Top Crossing Highlights with the 3 subcards
               stacked vertically. Height matched to the Crossings panel. -->
          <ChartPanel
            class="tw-area-highlights tw-compact panel-top-visitors"
            :title="topHighlightsTitle"
            icon="bi-calendar2-event"
            :height="360"
            :empty="!results"
            :accent="zoneColor"
          >
            <ChartTopVisitorHighlights
              :days="effectiveFormattedTopVisitorDays"
              :maxDayCount="effectiveMaxDayCount"
              :hours="effectiveFormattedTopVisitorHours"
              :maxHourCount="effectiveMaxHourCount"
              :busiestAvgHours="effectiveFormattedBusiestAvgHours"
              :maxAvgVisitorsPerHour="effectiveMaxAvgPerHour"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
            />
          </ChartPanel>

          <!-- Row 2, left (1/2): Heatmap. Allowed to be a touch taller than
               the right column's stacked pair. -->
          <ChartPanel
            class="tw-area-heatmap"
            :title="$t('zoneDetail.panel.heatmap')"
            icon="bi-grid-3x3-gap"
            :height="520"
            :empty="!effectiveHeatmapData"
            :accent="zoneColor"
          >
            <HeatmapDetectionZone
              :heatmapChartData="effectiveHeatmapData"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
            />
          </ChartPanel>

          <!-- % of Total + Trends + Avg Comparison wrapped in one grid cell.
               The wrapper's flex-direction flips per mode (column for daily so
               they stack beside the heatmap; row for hourly so they share row 3). -->
          <div class="tw-area-trends-stack">
            <!-- For Entrance (1), Exit (2) and Combined EE (3) zones, the
                 zone's inbound count IS the store-visitor total — so the
                 share would always be 100%. The card is hidden there to
                 avoid a meaningless tautology. -->
            <ChartPanel
              v-if="![1, 2, 3].includes(detectionZoneTypeId)"
              class="tw-compact"
              :title="$t('zoneDetail.panel.pctOfTotal')"
              icon="bi-pie-chart"
              :height="140"
              :empty="!totalStoreVisitors"
              :emptyText="$t('zoneDetail.panel.pctOfTotalEmpty')"
              :accent="zoneColor"
            >
              <div class="pct-of-total">
                <div class="pct-value" :style="{ color: zoneColor }">
                  {{ pctText(tripwirePctOfTotal, 1) }}
                </div>
                <div class="pct-context">
                  {{ $t('zoneDetail.panel.pctOfTotalContext', { n: numText(results?.tripwire_total_in || 0), total: numText(totalStoreVisitors) }) }}
                </div>
              </div>
            </ChartPanel>

            <!-- Estimated Avg Dwell (Mode C — Little's Law). Only shown when
                 the zone has explicitly opted in via DetectionZoneSettings.
                 The backend refuses to return a value when the underlying
                 data isn't trustworthy enough, so a populated number here is
                 always either "medium" or "high" confidence. Nothing → the
                 empty-state explains why. -->
            <ChartPanel
              v-if="useTripwireForDwell"
              class="tw-compact"
              :title="dwellPanelTitle"
              icon="bi-stopwatch"
              :height="140"
              :empty="hideDwellInAllMode || !results?.tripwire_dwell_estimate_seconds"
              :emptyText="hideDwellInAllMode ? dwellAllModeNote : dwellEmptyStateText"
              :accent="zoneColor"
            >
              <div class="dwell-estimate">
                <div class="dwell-value-row">
                  <span class="dwell-value" :style="{ color: zoneColor }">
                    {{ formatDwellSeconds(results.tripwire_dwell_estimate_seconds) }}
                  </span>
                  <!-- Confidence chip — self-describing (the label
                       text already says "high confidence" / "medium
                       confidence"). Tooltip removed; the underlying
                       methodology is documented in the help modal,
                       not on every chip. -->
                  <span
                    v-if="results?.tripwire_dwell_confidence"
                    :class="['dwell-conf', `conf-${results.tripwire_dwell_confidence}`]"
                  >
                    {{ confidenceText(results.tripwire_dwell_confidence) }}
                  </span>
                </div>

                <!-- Explainer for entrance / exit / Combined-EE zones so the
                     "Avg Store Visit Length" label is unambiguous. -->
                <div v-if="isEntranceLikeZoneType" class="dwell-context">
                  <i class="bi bi-info-circle"></i>
                  {{ $t('zoneDetail.dwell.storeContext') }}
                </div>

                <div v-if="transitSourceZoneName" class="dwell-context">
                  <i class="bi bi-arrow-right-circle"></i>
                  {{ $t('zoneDetail.dwell.transitFrom', { zone: transitSourceZoneName }) }}
                </div>
                <!-- dwell-context line is already self-explanatory
                     ("Based on N days of … crossings"). The old
                     dwellMethodTooltip text was outdated (described
                     a different estimator than what's actually used)
                     so we removed it rather than rewrite. The
                     confidence-chip tooltip above still carries the
                     methodology context. -->
                <div class="dwell-context">
                  <i class="bi bi-info-circle"></i>
                  {{ $t(transitSourceZoneName ? 'zoneDetail.dwell.basedOnPaired' : 'zoneDetail.dwell.basedOnInOut', { n: results.tripwire_dwell_sample_days || 0 }, results.tripwire_dwell_sample_days || 0) }}
                </div>
                <div v-for="w in perWireBiasWarnings" :key="w.tripwire_id"
                     class="dwell-context dwell-wire-warning">
                  <i class="bi bi-exclamation-triangle"></i>
                  {{ $t('zoneDetail.dwell.wireBias', { id: w.tripwire_id, pct: pctText(w.bias_pct, 1), nIn: w.n_in, nOut: w.n_out }) }}
                </div>

                <!-- Per-hour breakdown — hidden for entrance-like zones,
                     where the hourly estimator is model-mismatched (typical
                     store visits are longer than the estimator's 1-hour
                     window, producing meaningless per-hour values). -->
                <div v-if="isBurstyTraffic && hourlyBreakdown.length > 0 && !isEntranceLikeZoneType"
                     class="dwell-context dwell-wire-warning">
                  <div>
                    <i class="bi bi-graph-up-arrow"></i>
                    {{ $t('zoneDetail.dwell.bursty') }}
                  </div>
                  <div class="hourly-chip-row">
                    <Tooltip
                      v-for="entry in hourlyBreakdown"
                      :key="entry.hour"
                      :text="$t('zoneDetail.dwell.hourTip', { n: entry.n_events, hour: entry.hourLabel })"
                    >
                      <span
                        class="hourly-chip"
                        :class="{ 'hourly-chip-peak': entry.is_peak }"
                      >
                        <strong>{{ entry.hourLabel }}</strong> {{ entry.dwellLabel }}
                      </span>
                    </Tooltip>
                  </div>
                </div>
                <div v-else-if="isBurstyTraffic && !isEntranceLikeZoneType" class="dwell-context dwell-wire-warning">
                  <i class="bi bi-graph-up-arrow"></i>
                  {{ $t('zoneDetail.dwell.burstyNoData') }}
                </div>

                <!-- Bimodal block — hidden for entrance-like zones. The
                     mixture is built from a cross-correlation pairwise-
                     difference histogram of unrelated in/out events (no
                     TrackId on entrance-tripwire dwell records). On an
                     entrance with clustered arrival/departure traffic
                     (e.g. lunch rush) the histogram's modes reflect
                     temporal clustering, not visitor segmentation —
                     showing "60% of visitors stay 3 min" is actively
                     misleading. Product / Demo / Display zones keep the
                     block because their typical visits fit inside the
                     pairing window and tracking is less fragmented. -->
                <div v-if="isBimodalDwell && !isEntranceLikeZoneType" class="dwell-context dwell-wire-warning">
                  <i class="bi bi-bar-chart-steps"></i>
                  <template v-if="mixtureBreakdown">
                    <i18n-t keypath="zoneDetail.dwell.twoTypes" scope="global">
                      <template #shortPct><strong>{{ pctText(mixtureBreakdown.shortPct) }}</strong></template>
                      <template #longPct><strong>{{ pctText(mixtureBreakdown.longPct) }}</strong></template>
                      <template #w1>{{ mixtureBreakdown.W1 }}</template>
                      <template #w2>{{ mixtureBreakdown.W2 }}</template>
                    </i18n-t>
                  </template>
                  <template v-else>
                    {{ $t('zoneDetail.dwell.twoTypesModes', { modes: bimodalPeakSummary }) }}
                  </template>
                </div>
              </div>
            </ChartPanel>

            <ChartPanel
              class="tw-compact panel-trends"
              :title="trendsPanelTitle"
              icon="bi-activity"
              :empty="!effectiveTrendTotal"
              :accent="zoneColor"
              :height="'auto'"
            >
              <ChartVisitorTrend
                v-if="effectiveTrendTotal"
                :trend-total="effectiveTrendTotal"
                :trend-walkbys="null"
                :trend-shorts="null"
                :trend-clears="null"
                :zoneColor="zoneColor"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
                :durationSegmentationStrategy="results?.visitors_duration_segmentation?.strategy || 'insufficient_data'"
                :totalTrendLabel="useCrossingsLabel ? $t('zoneDetail.panel.totalInboundTrend') : $t('zoneDetail.panel.totalVisitorTrend')"
                :hideSegmentationTrends="true"
              />
            </ChartPanel>

            <ChartPanel
              class="tw-compact"
              :title="avgComparisonTitle"
              icon="bi-bar-chart"
              :height="220"
              :empty="!results || effectiveAvgPerDay == null || effectiveAvgSameWeekday == null"
              :accent="zoneColor"
            >
              <ChartAvgVisitorComparison
                v-if="effectiveAvgPerDay != null && effectiveAvgSameWeekday != null"
                :avg-all="effectiveAvgPerDay"
                :avg-same-weekday="effectiveAvgSameWeekday"
                :zoneColor="zoneColor"
                :timeSpan="durationText"
                :currentWeekday="results.current_weekday ?? 0"
              />
            </ChartPanel>
          </div>
        </div>

        <!-- Polygon / polygon+tripwire layout (unchanged from before — only
             skipped when the zone is tripwire-only and the dedicated grid
             above takes over). -->
        <template v-if="!useTripwireMode">
          <!-- Row 2.5: Crossings panel for polygon+tripwire combo zones -->
          <ChartPanel
            v-if="hasTripwireActivity"
            :title="crossingsPanelTitle"
            icon="bi-arrow-left-right"
            :height="420"
            :note="tripwireMarkerCaption || ''"
            :empty="!tripwireChartData"
            :accent="zoneColor"
            style="margin-top:16px;"
          >
            <div class="grid-panel-toolbar tripwire-toolbar">
              <div class="tripwire-totals">
                <span class="tw-pill tw-in">
                  <i class="bi bi-arrow-down-right"></i>
                  {{ tripwireInLabel }}: <b>{{ results?.tripwire_total_in || 0 }}</b>
                </span>
                <span class="tw-pill tw-out">
                  <i class="bi bi-arrow-up-right"></i>
                  {{ tripwireOutLabel }}: <b>{{ results?.tripwire_total_out || 0 }}</b>
                </span>
              </div>
              <data-granularity style="margin-top: 6px;" @updateDataGranularity="updateTripwireGranularity" />
              <label v-if="weatherEnabled && tripwireGranularity === 'daily'" class="wx-overlay-toggle" :title="$t('zoneDetail.weather.crossingsTip')">
                <input type="checkbox" v-model="weatherOverlay" />
                <i class="bi bi-thermometer-half"></i> {{ $t('zoneDetail.weather.toggle') }}
              </label>
            </div>
            <ChartLineTripwireCrossingsDetectionZone
              v-if="tripwireChartData"
              :chart-data="tripwireChartData"
              :timeSpan="durationText"
              :in-label="tripwireInLabel"
              :out-label="tripwireOutLabel"
              :show-weather="weatherEnabled && weatherOverlay && tripwireGranularity === 'daily'"
              :weather-by-date="weatherDailyTemp"
              :markers="zoneMarkers"
            />
          </ChartPanel>

          <!-- Row 3: Visitor Trends with all four sub-trends (polygon path) -->
          <ChartPanel
            :title="trendsPanelTitle"
            icon="bi-activity"
            :empty="!(results?.trend_total && results?.trend_walkbys && results?.trend_shorts && results?.trend_clears)"
            :accent="zoneColor"
            :height="'auto'"
            class="panel-trends"
            style="margin-top: 16px;"
          >
            <ChartVisitorTrend
              v-if="results?.trend_total && results?.trend_walkbys && results?.trend_shorts && results?.trend_clears"
              :trend-total="results.trend_total"
              :trend-walkbys="results.trend_walkbys"
              :trend-shorts="results.trend_shorts"
              :trend-clears="results.trend_clears"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :detectionZoneTypeName="detectionZoneTypeName"
              :durationSegmentationStrategy="results?.visitors_duration_segmentation?.strategy || 'insufficient_data'"
            />
          </ChartPanel>

          <!-- Checkout throughput — this till's own page. How many real transactions
               it gets through by hour, and how long each one takes. Not the
               store-level barlist: one row is not a comparison.
               Checkout Interaction zones (type 5) only. -->
          <CheckoutTransactionsPanel
            v-if="detectionZoneTypeId === 5"
            :data="checkoutTransactions"
            :zone-name="detectionZone?.DetectionZoneName"
            :timeSpan="durationText"
            :accent="zoneColor"
          />

          <!-- Row 3.5: Service response — "and did anyone come to them?"
               Sits AFTER the zone's own behaviour (counts, duration, trend) because it
               is an outcome of that behaviour, and BEFORE the timing block because its
               own chart answers a timing question. Product (6) + Demo (7) only.
               Renders nothing at all while the state is `pending` — a zone that
               qualifies but has no nightly figure yet must not show a zero.
               docs/reference/service_response_measurement.md -->
          <ServiceResponseZonePanel
            v-if="[6, 7].includes(detectionZoneTypeId)"
            :data="serviceResponse"
            :timeSpan="durationText"
            :detectionZoneTypeName="detectionZoneTypeName"
            :accent="zoneColor"
          />


          <!-- Row 4: Heatmap + dwell-segmentation-per-day stacked pair -->
          <ChartGrid class="zone-row4" style="margin-top: 16px;">
            <ChartPanel
              :title="$t('zoneDetail.panel.heatmap')"
              icon="bi-grid-3x3-gap"
              :height="640"
              :empty="!effectiveHeatmapData"
              :accent="zoneColor"
            >
              <HeatmapDetectionZone
                :heatmapChartData="effectiveHeatmapData"
                :timeSpan="durationText"
                :detectionZoneTypeName="detectionZoneTypeName"
              />
            </ChartPanel>

            <div class="chart-vert-stack">
              <ChartPanel
                :title="$t('zoneDetail.panel.segCounts')"
                icon="bi-bar-chart"
                :height="310"
                :empty="!results?.daily_lingerer_chart_data || results?.visitors_duration_segmentation?.strategy === 'insufficient_data'"
                :emptyText="$t('zoneDetail.panel.segEmpty')"
                :accent="zoneColor"
              >
                <ChartDurationSegmentationCountsPerDay
                  :durations="results.daily_lingerer_chart_data"
                  :timeSpan="durationText"
                  :detectionZoneTypeName="detectionZoneTypeName"
                />
              </ChartPanel>

              <ChartPanel
                :title="$t('zoneDetail.panel.segMix')"
                icon="bi-graph-up"
                :height="310"
                :empty="!results?.daily_lingerer_chart_data || results?.visitors_duration_segmentation?.strategy === 'insufficient_data'"
                :emptyText="$t('zoneDetail.panel.segEmpty')"
                :accent="zoneColor"
              >
                <ChartDurationSegmentationPercentagePerDay
                  :durations="results.daily_lingerer_chart_data"
                  :timeSpan="durationText"
                  :detectionZoneTypeName="detectionZoneTypeName"
                />
              </ChartPanel>
            </div>
          </ChartGrid>
        </template>

        <!-- Row 5: Top Highlights + Average Comparison. Polygon / polygon+
             tripwire layout only — for tripwire-only zones these two panels
             live inside the dedicated grid above. -->
        <ChartGrid v-if="!useTripwireMode" style="margin-top: 16px;">
          <ChartPanel
            :title="topHighlightsTitle"
            icon="bi-calendar2-event"
            class="panel-top-visitors"
            :empty="!results"
            :accent="zoneColor"
          >
            <ChartTopVisitorHighlights
              :days="effectiveFormattedTopVisitorDays"
              :maxDayCount="effectiveMaxDayCount"
              :hours="effectiveFormattedTopVisitorHours"
              :maxHourCount="effectiveMaxHourCount"
              :busiestAvgHours="effectiveFormattedBusiestAvgHours"
              :maxAvgVisitorsPerHour="effectiveMaxAvgPerHour"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
            />
          </ChartPanel>

          <ChartPanel
            :title="avgComparisonTitle"
            icon="bi-bar-chart"
            :height="220"
            :empty="!results"
            :accent="zoneColor"
          >
            <ChartAvgVisitorComparison
              :avg-all="effectiveAvgPerDay"
              :avg-same-weekday="effectiveAvgSameWeekday"
              :zoneColor="zoneColor"
              :timeSpan="durationText"
              :currentWeekday="results.current_weekday"
            />
          </ChartPanel>
        </ChartGrid>
      </template>
      <!-- /guard -->
      </template>

    </Section>

    <!-- loader overlay -->
    <div v-if="isLoading" class="loader-overlay">
      <div class="loader"></div>
    </div>
  </div>

  <!-- Allen's Insights modal -->
  <teleport to="body">
    <div v-if="showAllensInsights" class="modal-overlay" @click.self="toggleAllensInsights">
      <div class="modal-window">
        <store-analytics-by-allen
          :reportHtml="storeAnalytics"
          :selectedDuration="duration"
          :dateRangeMode="dateRangeMode"
          :origin="'Zone'"
          :zoneName="detectionZone.DetectionZoneName"
          @close="toggleAllensInsights"
        />
      </div>
    </div>
  </teleport>
</template>

<script>
    import { onMounted, onUnmounted, ref, computed, watch } from 'vue'
    import { useRoute } from 'vue-router'
    import { setAllenContext } from '@/composables/allenContext'

    import { useDetectionZoneStore } from '@/stores/DetectionZone'
    import { useDetectionZoneTypeStore } from '@/stores/DetectionZoneTypeStore'
    import { useOpeningHoursStore } from '@/stores/OpeningHoursStore'

    import { getDetectionZoneData, getTotalStoreVisitors, getAllenInsight, getWeatherInsight, getServiceResponse, getCheckoutTransactions } from "@/api/eventsAPI.js"
    import ServiceResponseZonePanel from '@/components/ServiceResponseZonePanel.vue'
    import CheckoutTransactionsPanel from '@/components/CheckoutTransactionsPanel.vue'
    import ZoneWeatherCard from './weather/ZoneWeatherCard.vue'
    import { bucketVisitsByTemp } from '@/weather/weatherPresentation'
    import { detection_zone_colors_by_name_solid } from '@/constants.js'
    import { hasAnyTripwire } from '@/utils/tripwireShape'

    import StoreAnalyticsByAllen from './StoreAnalyticsByAllen.vue'
    import ChartTotalDurationSegmentation from './ChartTotalDurationSegmentation.vue'
    import ChartDurationSegmentationCountsPerDay from './ChartDurationSegmentationCountsPerDay.vue'
    import ChartDurationSegmentationPercentagePerDay from './ChartDurationSegmentationPercentagePerDay.vue'
    import HeatmapDetectionZone from './HeatmapDetectionZone.vue'
    import ChartLineTotalCountsDetectionZone from './ChartLineTotalCountsDetectionZone.vue'
    import ChartLineAverageDurationDetectionZone from './ChartLineAverageDurationDetectionZone.vue'
    import ChartLineTripwireCrossingsDetectionZone from './ChartLineTripwireCrossingsDetectionZone.vue'
    import ChartVisitorSegmentation from './ChartVisitorSegmentation.vue'
    import ChartZoneOccupation from './ChartZoneOccupation.vue'
    import ChartAvgVisitorComparison from './ChartAvgVisitorComparison.vue'
    import ChartTopVisitorHighlights from './ChartTopVisitorHighlights.vue'
    import ChartVisitorTrend from './ChartVisitorTrend.vue'

    import DatePicker from './DatePicker.vue'
    import DurationDropdown from './DurationDropdown.vue'
    import DataGranularity from './DataGranularityRadioButton.vue'
    import DropdownOpeningHoursMode from './DropdownOpeningHoursMode.vue'

    // shared UI
    import Section from '@/components/ui/Section.vue'
    import ControlHead from '@/components/ui/ControlHead.vue'
    import ChartGrid from '@/components/ui/ChartGrid.vue'
    import ChartPanel from '@/components//ui/ChartPanel.vue'
    import Tooltip from '@/components/ui/Tooltip.vue'
    import ChangeNotice from '@/components/ui/ChangeNotice.vue'
    import { useChangesTodayStore } from '@/stores/ChangesToday'
    import { useUserStore } from '@/stores/UserStore'
    import { NOTICES, ZONE_TYPE_NAMES, typeChangeOf, settingChanged } from '@/zones/changesToday'
    import { markerCaption } from '@/zones/zoneMarkers'
    import { apiClientDB } from '@/config_sessions'
    import { useI18n } from 'vue-i18n'
    import { currentLanguage } from '@/i18n'
    import { formatNumber, formatPercent, formatDuration, formatWeekday } from '@/i18n/format'
    import { zoneTypeLabel } from '@/i18n/zoneTypes'


    export default {
        components: {
            ChangeNotice,
            Tooltip,
            ServiceResponseZonePanel,
            CheckoutTransactionsPanel,
            StoreAnalyticsByAllen,
            ChartTotalDurationSegmentation,
            ChartDurationSegmentationCountsPerDay,
            ChartDurationSegmentationPercentagePerDay,
            HeatmapDetectionZone,
            ChartLineTotalCountsDetectionZone,
            ChartLineAverageDurationDetectionZone,
            ChartLineTripwireCrossingsDetectionZone,
            ZoneWeatherCard,
            ChartVisitorSegmentation,
            ChartZoneOccupation,
            ChartAvgVisitorComparison,
            ChartTopVisitorHighlights,
            ChartVisitorTrend,
            DurationDropdown,
            DatePicker,
            DataGranularity,
            DropdownOpeningHoursMode,
            Section,
            ControlHead,
            ChartGrid,
            ChartPanel,
        },
        
        props: {
            
        },

        setup(props, { emit }) {
            const { t, te } = useI18n()
            const tr = t   // `t` is shadowed by a local zone-type name in a few computeds below
            // English keeps its own formats exactly as before; Dutch goes through format.js.
            const isEn = () => currentLanguage() === 'en'
            const pctText = (v, decimals = 0) => (isEn()
                ? `${decimals ? Number(v).toFixed(decimals) : v}%`
                : formatPercent(v, { decimals }))
            const numText = (n) => (isEn() ? n : formatNumber(n))
            const confidenceText = (c) => (te(`zoneDetail.dwell.confidence.${c}`)
                ? t(`zoneDetail.dwell.confidence.${c}`) : `${c} confidence`)
            // The zone type a changed zone was / is now, as shown on screen.
            const typeNameOf = (id) => (ZONE_TYPE_NAMES[id]
                ? (isEn() ? ZONE_TYPE_NAMES[id] : zoneTypeLabel(id))
                : t('zoneDetail.otherType'))
            const detectionZoneStore = useDetectionZoneStore()
            const detectionZoneTypeStore = useDetectionZoneTypeStore()
            const openingHoursStore = useOpeningHoursStore()

            const route = useRoute()
            const detectionZoneId = computed(() => parseInt(route.params.DetectionZoneId))
            // Zone identity 6.2: changed type today -> notice instead of the charts.
            const changesToday = useChangesTodayStore()
            const userStore = useUserStore()
            changesToday.ensureLoaded(userStore.currentCompany?.CompanyId ?? null)
            const typeChange = computed(() => (detectionZone.value
                ? typeChangeOf(changesToday.register, { key: detectionZone.value.ZoneKey }) : null))
            const openingHoursChanged = computed(() => settingChanged(changesToday.register, 'opening_hours'))
            const thresholdChanged = computed(() => !!(detectionZone.value
                && ((changesToday.register.settings || {}).thresholds || {}).zones?.length
                && settingChanged(changesToday.register, 'thresholds', detectionZone.value.ZoneKey)))
            // Service response for this zone (docs/reference/service_response_measurement.md).
            // null = nothing known yet, which the panel renders as nothing at all.
            const serviceResponse = ref(null)
            const checkoutTransactions = ref(null)

            const allZones = ref({})
            const detectionZone = ref("")
            const detectionZoneTypeId = ref(null)

            // Feed the docked Ask Allen the zone name so its openers read "…in {zone}".
            watch(detectionZone, (z) => {
                if (z && z.DetectionZoneName) setAllenContext('zone', z.DetectionZoneName)
            })
            const detectionZoneTypeName = ref("")
            const sameIdInOutSetting = ref(false)
            // Dwell Group opt-in (Mode C). Mirrors DetectionZoneSettings.UseTripwireForDwell.
            const useTripwireForDwell = ref(false)

            // Empty-state copy for the dwell card. Branches on the backend's
            // tripwire_dwell_pending_reason so the user sees a specific
            // explanation instead of the generic "Not enough data yet".
            const dwellEmptyStateText = computed(() => {
                const reason = results.value?.tripwire_dwell_pending_reason
                if (reason === 'transition_no_source') return t('zoneDetail.dwell.empty.noSource')
                if (reason === 'transition_bad_source_key') return t('zoneDetail.dwell.empty.badSource')
                if (reason === 'transition_self_loop') return t('zoneDetail.dwell.empty.selfLoop')
                if (reason === 'transition_not_enough_data') return t('zoneDetail.dwell.empty.notEnoughTransit')
                return t('zoneDetail.dwell.empty.default')
            })

            // When this zone is configured in Transition mode, surface the
            // source zone's NAME so the dashboard card can show "Transit from
            // <Zone>" instead of the bare number. Populated alongside
            // useTripwireForDwell when the zone's settings load. Stays
            // null for zones configured in regular dwell mode (the caption
            // is then hidden by v-if).
            const transitSourceZoneName = ref(null)

            // Per-wire bias diagnostics surfaced beneath the dwell card.
            // Show only wires with bias > 10% (below that it's normal
            // sampling noise on a few hundred crossings).
            const perWireBiasWarnings = computed(() => {
                const list = results.value?.tripwire_dwell_per_wire_bias
                if (!Array.isArray(list)) return []
                return list.filter(w => (w?.bias_pct || 0) > 10)
            })

            // Stationarity warning — surfaces "this zone has bursty
            // traffic" when the backend's chi-square hourly test flags
            // it. The single dwell number we publish is a steady-state
            // average, so for zones with strong lunch peaks etc. the
            // number is less trustworthy.
            const isBurstyTraffic = computed(() =>
                results.value?.tripwire_dwell_stationarity?.is_bursty === true
            )

            // Per-hour breakdown surfaced for bursty zones. Each entry
            // gets a precomputed display label and an `is_peak` flag
            // (top 20% of event counts) so the template stays tidy.
            const hourlyBreakdown = computed(() => {
                const list = results.value?.tripwire_dwell_per_hour
                if (!Array.isArray(list) || list.length === 0) return []
                const counts = list.map(e => e.n_events || 0)
                const peakThreshold = counts.length
                    ? counts.slice().sort((a, b) => b - a)[Math.max(0, Math.floor(counts.length * 0.2) - 1)]
                    : 0
                const fmt = (s) => (isEn()
                    ? (s < 60 ? `${Math.round(s)}s` : `${(s/60).toFixed(1)}m`)
                    : formatDuration(s))
                return list.map(e => ({
                    hour: e.hour,
                    hourLabel: isEn() ? `${e.hour}h` : `${String(e.hour).padStart(2, '0')}:00`,
                    n_events: e.n_events,
                    dwellLabel: fmt(e.dwell_seconds || 0),
                    is_peak: (e.n_events || 0) >= peakThreshold && peakThreshold > 0,
                }))
            })

            // Bimodality flag + a short human-readable summary of where the
            // two peaks sit. Shown under the dwell card when the detector
            // sees two distinct visitor populations (e.g. short glancers
            // vs. real engagers).
            const isBimodalDwell = computed(() =>
                results.value?.tripwire_dwell_bimodality?.is_bimodal === true
            )
            const bimodalPeakSummary = computed(() => {
                const peaks = results.value?.tripwire_dwell_bimodality?.peak_locations_s
                if (!Array.isArray(peaks) || peaks.length < 2) return ''
                const fmt = (s) => (isEn()
                    ? (s < 60 ? `${s.toFixed(0)}s` : `${(s/60).toFixed(1)}min`)
                    : formatDuration(s))
                return peaks.map(fmt).join(` ${t('zoneDetail.dwell.and')} `)
            })

            // When the 2-component mixture fit succeeded, format both
            // modes + the mix weights as percentages for display.
            const mixtureBreakdown = computed(() => {
                const m = results.value?.tripwire_dwell_mixture
                if (!m || m.mix_weight_short == null) return null
                const fmt = (s) => (isEn()
                    ? (s < 60 ? `${s.toFixed(0)}s` : `${(s/60).toFixed(1)}min`)
                    : formatDuration(s))
                const shortPct = Math.round(m.mix_weight_short * 100)
                return {
                    W1: fmt(m.W1_seconds),
                    W2: fmt(m.W2_seconds),
                    shortPct,
                    longPct: 100 - shortPct,
                }
            })

            // Entrance / Exit / Combined-EE zones occupy a special place in
            // the tripwire dwell estimator. Two consequences for display:
            //
            //   - The per-hour breakdown is broken for these zones because
            //     typical store visits are longer than the estimator's
            //     one-hour window. Hide it entirely.
            //
            //   - The bimodality/mixture report is *also* unreliable here,
            //     for a separate reason: it's built from a pairwise-
            //     difference cross-correlation histogram of unrelated
            //     in/out events (entrance-tripwire dwell records carry no
            //     TrackId — see process_tripwire_dwell_events in
            //     bgtaskdatafetching.py). On entrances with clustered
            //     arrival/departure traffic the histogram's modes reflect
            //     temporal clustering (lunch rush etc.), not actual
            //     visitor segmentation. The single-number Little's Law
            //     headline is the most defensible reading here because it
            //     uses day-aggregate counts and doesn't depend on per-
            //     visitor identification.
            const isEntranceLikeZoneType = computed(() => {
                const t = detectionZoneTypeName.value
                return t === 'Entrance Zone'
                    || t === 'Exit Zone'
                    || t === 'Combined Entrance Exit Zone'
            })

            // Panel title swaps between two semantics. For product / display
            // / etc. tripwires "Avg Dwell" is correct (time inside a specific
            // area). For entrance-like zones it's the customer's total time
            // inside the store, a different metric that deserves a different
            // name so users don't confuse the two.
            const dwellPanelTitle = computed(() =>
                isEntranceLikeZoneType.value
                    ? t('zoneDetail.dwell.titleStore')
                    : t('zoneDetail.dwell.title')
            )

            const results = ref({})

            // Checkout queue (line length from waiting-zone occupancy overlap) —
            // only present on the zone doc for Checkout Waiting zones (type 4).
            const checkoutQueue = computed(() => {
                if (detectionZoneTypeId.value !== 4) return null
                return results.value?.checkout_queue || null
            })
            const checkoutQueueVerdict = computed(() => {
                const p = Number(checkoutQueue.value?.pct_time_ge2 ?? 0)
                if (p < 5) return t('zoneDetail.queue.rarely')
                if (p < 15) return t('zoneDetail.queue.occasionally')
                return t('zoneDetail.queue.often')
            })

            const totalStoreVisitors = ref(0)
            const conversionRate = ref(0.1)
            const productValue = ref(0)

            const storeAnalytics = ref("")
            const jobStatus    = ref(null)
            const showAllensInsights = ref(false)

            const dateRangeMode = ref('duration')
            const clearDatePicker = ref(null)
            const startDate = ref(null)
            const endDate = ref(null)
            const countsGranularity   = ref('daily')
            const avgDurGranularity   = ref('daily')
            const tripwireGranularity = ref('daily')
            const duration = ref()

            // The page's numeric duration -> the ETL's Redis slot name. Same slot
            // vocabulary the rest of the pipeline uses (docs: bgtaskdatafetching
            // time_slots); anything unrecognised falls back to the 30-day view rather
            // than requesting a key that cannot exist.
            const serviceResponseSlot = computed(() => ({
                1: 'yesterday',
                7: 'past_7_days',
                30: 'past_30_days',
                90: 'past_90_days',
                365: 'past_365_days',
                1000: 'this_week',
                2000: 'this_month',
                3000: 'last_week',
                4000: 'last_month',
            }[duration.value] || 'past_30_days'))
            // The period as shown (charts get it as their timeSpan). Kept as the
            // duration's key so a language switch re-renders it.
            const durationKey = ref(null)
            const durationText = computed(() => (durationKey.value ? t(`zoneDetail.duration.${durationKey.value}`) : ''))
            const openingHoursMode = ref('only_regular_opening_hours')
            // Dwell is a while-we're-open metric. Outside opening hours the entrance still
            // logs staff (opening up / closing) — those crossings aren't visits and would
            // dilute the arrival rate, overstating dwell. So we don't show a dwell figure in
            // "All data" mode, only under the opening-hours views. Counts stay all=all; this
            // gate is dwell-only. (See the store-dwell root-cause analysis, cid-8 Aug 2026.)
            const hideDwellInAllMode = computed(() => openingHoursMode.value === 'all')
            const dwellAllModeNote = computed(() => t('zoneDetail.dwell.allModeNote'))
            const disabledOpeningHourModes = ref([])
            
            const isLoading = ref(false)

            const isMounted = ref(true)
            let currentFetchController = null
            let latestRequestId = 0
            const POLL_INTERVAL_MS = 2000

            const detectionZoneTypes = computed(() => detectionZoneTypeStore.detectionzonetypes)

            const countsChartData = computed(() =>
                countsGranularity.value === 'daily'
                    ? results.value.daily_chart_and_list_data
                    : results.value.hourly_chart_and_list_data
            )
            const avgDurChartData = computed(() =>
                avgDurGranularity.value === 'daily'
                    ? results.value.daily_chart_and_list_data
                    : results.value.hourly_chart_and_list_data
            )

            // Tripwire chart driven by the new tripwire_*_chart_data fields
            // produced by the nightly ETL. Polygon-only zones get an empty
            // dict here, which is what the v-if below uses to hide the panel.
            const tripwireChartData = computed(() =>
                tripwireGranularity.value === 'daily'
                    ? (results.value?.tripwire_daily_chart_data || {})
                    : (results.value?.tripwire_hourly_chart_data || {})
            )

            // Settings step 2c: the days this zone was answered "Something changed at
            // this spot" — a dashed line on its time charts (GET /zone_markers/).
            const zoneMarkers = ref([])
            watch(() => detectionZone.value?.ZoneKey, async (key) => {
                zoneMarkers.value = []
                if (key == null) return
                try {
                    const r = await apiClientDB.get('/zone_markers/', {
                        params: { zoneKey: key },
                        headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
                    })
                    if (detectionZone.value?.ZoneKey === key && Array.isArray(r.data)) zoneMarkers.value = r.data
                } catch (e) {
                    // a marker is optional: never break the page
                }
            }, { immediate: true })
            const zoneAxis = (data) => Object.entries(data || {})
                .filter(([, z]) => z && z[detectionZone.value?.DetectionZoneName]).map(([d]) => d)
            const countsMarkerCaption = computed(() => markerCaption(zoneMarkers.value, zoneAxis(countsChartData.value)))
            const avgDurMarkerCaption = computed(() => markerCaption(zoneMarkers.value, zoneAxis(avgDurChartData.value)))
            const tripwireMarkerCaption = computed(() =>
                markerCaption(zoneMarkers.value, Object.keys(tripwireChartData.value || {}).sort()))

            // --- Weather insight (nightly-precomputed; per-zone signals + temp series) ---
            const weatherZonesPayload = ref(null)   // /weather_insight/ zones part
            const weatherDailyTemp = ref(null)      // {date: daytime-mean °C}
            const weatherOverlay = ref(true)        // temperature overlay toggle (daily)
            // Weather-enabled iff the store has a configured location (route returned data).
            const weatherEnabled = computed(() =>
                !!(weatherZonesPayload.value && !weatherZonesPayload.value.status))
            const weatherZoneSignals = computed(() => weatherZonesPayload.value?.zones || [])
            // "Visits by temperature" mini-curve for THIS zone, from real daily counts
            // (polygon or tripwire) joined to the daytime-temp series. Null when sparse.
            const zoneWeatherCurve = computed(() => {
                if (!weatherEnabled.value || !weatherDailyTemp.value) return null
                const zn = detectionZone.value?.DetectionZoneName
                const countsByDate = {}
                if (useTripwireMode.value) {
                    const data = results.value?.tripwire_daily_chart_data || {}
                    for (const [d, row] of Object.entries(data)) countsByDate[d] = row?.zone_in_count ?? 0
                } else {
                    const data = results.value?.daily_chart_and_list_data || {}
                    for (const [d, zmap] of Object.entries(data)) {
                        if (zmap && zmap[zn]) countsByDate[d] = zmap[zn].zone_in_count ?? 0
                    }
                }
                return bucketVisitsByTemp(countsByDate, weatherDailyTemp.value)
            })
            const loadWeatherInsight = async () => {
                try {
                    const [zones, daily] = await Promise.all([
                        getWeatherInsight({ part: 'zones' }),
                        getWeatherInsight({ part: 'daily' }),
                    ])
                    weatherZonesPayload.value = zones || null
                    weatherDailyTemp.value = (daily && !daily.status) ? daily : null
                } catch (e) {
                    console.error('weather insight load failed', e)
                }
            }

            // Show the tripwire panel when there is any tripwire activity in
            // the slot. Doing this against the totals (rather than the zone's
            // current geometry) is what carries the polygon→tripwire migration
            // case automatically: if the active zone is tripwire, totals > 0
            // and the panel shows; if it's polygon-only, totals are 0 and the
            // panel stays hidden.
            const hasTripwireActivity = computed(() => {
                const r = results.value || {}
                return (Number(r.tripwire_total_in) || 0) > 0
                    || (Number(r.tripwire_total_out) || 0) > 0
            })

            // Context-aware directional labels matching the live counter
            // conventions in Counters.vue (the user-facing meaning depends on
            // whether the zone is an entrance, exit, or generic flow point).
            const tripwireInLabel = computed(() => {
                const t = detectionZoneTypeName.value
                if (t === 'Combined Entrance Exit Zone') return tr('zoneDetail.direction.inStore')
                if (t === 'Entrance Zone') return tr('zoneDetail.direction.entries')
                if (t === 'Exit Zone') return tr('zoneDetail.direction.exits')
                return tr('zoneDetail.direction.inbound')
            })
            const tripwireOutLabel = computed(() => {
                const t = detectionZoneTypeName.value
                if (t === 'Combined Entrance Exit Zone') return tr('zoneDetail.direction.outStore')
                if (t === 'Entrance Zone') return tr('zoneDetail.direction.wrongWay')
                if (t === 'Exit Zone') return tr('zoneDetail.direction.wrongWay')
                return tr('zoneDetail.direction.outbound')
            })

            // ------- Tripwire-only mode rendering -------
            // A zone with a tripwire and no polygon is "tripwire-only" — its
            // polygon-derived analytics are all zero, so we hide the dwell /
            // segmentation panels and route the kept panels (Heatmap, Trends,
            // Top Highlights, Avg Comparison) onto the tripwire-equivalent
            // fields the backend now populates (tripwire_top_inbound_*,
            // tripwire_heatmap_chart_data, tripwire_trend_total, etc.).
            const useTripwireMode = computed(() => zoneHasTripwire.value && !zoneHasPolygon.value)

            // For Entrance(1) / Exit(2) / Combined EE(3) zones the tripwire
            // counts ARE visitors (people entering/leaving the store), so we
            // keep the "Visitor" labels. For any other zone type, tripwire
            // crossings are not visits — we use "Crossing" instead.
            const ENTRANCE_LIKE_TYPES = ['Entrance Zone', 'Exit Zone', 'Combined Entrance Exit Zone']
            const useCrossingsLabel = computed(() =>
                useTripwireMode.value && !ENTRANCE_LIKE_TYPES.includes(detectionZoneTypeName.value),
            )

            // Section / panel titles that flip based on the rule above
            // For Entrance / Exit / Combined EE zones a tripwire crossing IS
            // a visit (the person enters/leaves the store), so the panel reads
            // "Visits". For all other zone types it stays "Crossings".
            const crossingsPanelTitle = computed(() =>
                ENTRANCE_LIKE_TYPES.includes(detectionZoneTypeName.value) ? t('zoneDetail.panel.visits') : t('zoneDetail.panel.crossings')
            )
            const trendsPanelTitle    = computed(() => useCrossingsLabel.value ? t('zoneDetail.panel.crossingTrends') : t('zoneDetail.panel.visitorTrends'))
            const topHighlightsTitle  = computed(() => useCrossingsLabel.value ? t('zoneDetail.panel.topCrossing') : t('zoneDetail.panel.topVisitor'))
            const avgComparisonTitle  = computed(() => useCrossingsLabel.value ? t('zoneDetail.panel.avgCrossing') : t('zoneDetail.panel.avgVisitor'))

            // Effective data source for the kept panels — polygon when present,
            // otherwise the tripwire-derived equivalents from the backend.
            const effectiveHeatmapData = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_heatmap_chart_data
                    : results.value?.heatmap_chart_data
            )
            const effectiveTrendTotal = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_trend_total
                    : results.value?.trend_total
            )
            const effectiveAvgPerDay = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_avg_inbound_per_day
                    : results.value?.avg_visitors_per_day
            )
            const effectiveAvgSameWeekday = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_avg_inbound_same_weekday
                    : results.value?.avg_visitors_same_weekday
            )
            const effectiveMaxHourCount = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_inbound_hour
                    : results.value?.max_visitors_hour
            )
            const effectiveMaxDayCount = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_inbound_day
                    : results.value?.max_visitors_day
            )
            const effectiveMaxAvgPerHour = computed(() =>
                useTripwireMode.value
                    ? results.value?.tripwire_max_avg_inbound_per_hour
                    : results.value?.max_avg_visitors_per_hour
            )

            // Tripwire % of Total — the zone's inbound count divided by the
            // total store visitors (sum of polygon visits + tripwire inbounds
            // across all Entrance + Combined EE zones; computed by the backend
            // and fetched via getTotalStoreVisitors).
            const tripwirePctOfTotal = computed(() => {
                const inbound = Number(results.value?.tripwire_total_in || 0)
                const total = Number(totalStoreVisitors.value || 0)
                if (!total) return 0
                return (inbound / total) * 100
            })

            const zoneColor = computed(() => {
                if (!detectionZone.value || !detectionZone.value.DetectionZoneTypeId) return '#000000'

                const typeMatch = detectionZoneTypes.value.find(
                    t => t.DetectionZoneTypeId === detectionZone.value.DetectionZoneTypeId
                )

                detectionZoneTypeName.value = typeMatch?.DetectionZoneTypeName

                return detection_zone_colors_by_name_solid[detectionZoneTypeName.value] || '#000000'
            })

            // Zone geometry composition (polygon / tripwire / both). After phase 2,
            // the backend will populate DetectionZoneTripwirePoints; until then the
            // tripwire flag is always false.
            const tryParse = (str) => {
                if (!str) return null
                if (typeof str !== 'string') return str
                try { return JSON.parse(str) } catch { return null }
            }
            const zoneHasPolygon = computed(() => {
                const pts = tryParse(detectionZone.value?.DetectionZonePointArray)
                return Array.isArray(pts) && pts.length >= 3
            })
            const zoneHasTripwire = computed(() =>
                hasAnyTripwire(detectionZone.value?.DetectionZoneTripwirePoints)
            )
            const zoneGeometryLabel = computed(() => {
                if (zoneHasPolygon.value && zoneHasTripwire.value) return t('zoneDetail.geometry.both')
                if (zoneHasTripwire.value) return t('zoneDetail.geometry.line')
                return t('zoneDetail.geometry.zone')
            })

            // Format dwell seconds as humanized "Xm Ys" / "Xh Ym Zs".
            function formatDwellSeconds(secsRaw) {
                const s = Math.max(0, Math.round(Number(secsRaw || 0)))
                if (s <= 0) return '—'
                if (!isEn()) return formatDuration(s)
                if (s < 60) return `${s}s`
                const m = Math.floor(s / 60)
                const rs = s % 60
                if (m < 60) return rs > 0 ? `${m}m ${rs}s` : `${m}m`
                const h = Math.floor(m / 60)
                const rm = m % 60
                return rm > 0 ? `${h}h ${rm}m` : `${h}h`
            }

            const dwellConfidenceTooltip = computed(() => {
                const r = results.value || {}
                const conf = r.tripwire_dwell_confidence
                const days = r.tripwire_dwell_sample_days || 0
                const direction = r.tripwire_dwell_residual_direction
                if (!conf) return ''
                let imbalanceTxt = ''
                if (direction === 'missed_exits') imbalanceTxt = ' Some exits may have gone undetected, which can slightly inflate the estimate.'
                else if (direction === 'missed_entries') imbalanceTxt = ' Some entries may have gone undetected, which can slightly deflate the estimate.'
                const confBody = conf === 'high'
                    ? `Strong signal — based on ${days} day${days === 1 ? '' : 's'} of in/out crossings with balanced detection and steady traffic.`
                    : `Reasonable signal — based on ${days} day${days === 1 ? '' : 's'} of in/out crossings. Treat as directional rather than precise.`
                return `${conf.charAt(0).toUpperCase() + conf.slice(1)} confidence. ${confBody}${imbalanceTxt}`
            })

            const dwellMethodTooltip = computed(() =>
                "This is an estimate, not a direct measurement. It's derived from how many people cross this tripwire in each direction over the period, combined with the average number inside at any moment. " +
                "The estimate is most reliable when in/out detection is balanced and visitor traffic is steady. " +
                "Bursty patterns or imbalanced detection make it less precise, and the card hides itself entirely when the signal isn't strong enough to act on."
            )

            // "Sat, 17 October" / "15h–16h" in English (as always); Dutch "za 17 oktober" / "15:00–16:00".
            const dayLabel = (date) => {
                if (!isEn()) return `${formatWeekday(date, { short: true })} ${new Intl.DateTimeFormat('nl', { day: 'numeric', month: 'long' }).format(date)}`
                const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' })
                const day = date.getDate()
                const month = date.toLocaleDateString('en-GB', { month: 'long' })
                return `${weekday}, ${day} ${month}`
            }
            const hourRange = (hour, nextHour) => (isEn()
                ? `${hour}h–${nextHour}h`
                : `${String(hour).padStart(2, '0')}:00–${String(nextHour).padStart(2, '0')}:00`)

            const formattedTopVisitorDays = computed(() => {
                if (!results.value.top_visitor_days || !Array.isArray(results.value.top_visitor_days)) return []

                return results.value.top_visitor_days.map(dateStr => {
                    const date = new Date(dateStr)
                    return dayLabel(date)
                })
            })

            const formattedTopVisitorHours = computed(() => {
                if (!results.value.top_visitor_hours || !Array.isArray(results.value.top_visitor_hours)) return []

                return results.value.top_visitor_hours.map(datetimeStr => {
                    const date = new Date(datetimeStr)
                    const hour = date.getHours()
                    const nextHour = (hour + 1) % 24

                    return `${dayLabel(date)} | ${hourRange(hour, nextHour)}`
                })
            })

            const formattedBusiestAvgHours = computed(() => {
                if (!results.value.busiest_avg_hours || !Array.isArray(results.value.busiest_avg_hours)) return [];

                return results.value.busiest_avg_hours.map(hour => {
                    const nextHour = (hour + 1) % 24;
                    return hourRange(hour, nextHour);
                })
            })

            // Effective formatters that swap polygon ↔ tripwire source. Tripwire
            // fields use the same shapes (date-string list, datetime-string list,
            // hour-of-day int list) so the existing formatting logic applies.
            const effectiveFormattedTopVisitorDays = computed(() => {
                if (!useTripwireMode.value) return formattedTopVisitorDays.value
                const arr = results.value?.tripwire_top_inbound_days
                if (!Array.isArray(arr)) return []
                return arr.map(dateStr => {
                    const date = new Date(dateStr)
                    return dayLabel(date)
                })
            })
            const effectiveFormattedTopVisitorHours = computed(() => {
                if (!useTripwireMode.value) return formattedTopVisitorHours.value
                const arr = results.value?.tripwire_top_inbound_hours
                if (!Array.isArray(arr)) return []
                return arr.map(datetimeStr => {
                    const date = new Date(datetimeStr)
                    const hour = date.getHours()
                    const nextHour = (hour + 1) % 24
                    return `${dayLabel(date)} | ${hourRange(hour, nextHour)}`
                })
            })
            const effectiveFormattedBusiestAvgHours = computed(() => {
                if (!useTripwireMode.value) return formattedBusiestAvgHours.value
                const arr = results.value?.tripwire_busiest_avg_inbound_hours
                if (!Array.isArray(arr)) return []
                return arr.map(hour => {
                    const nextHour = (hour + 1) % 24
                    return hourRange(hour, nextHour)
                })
            })
            
            onMounted(async () => {
                isMounted.value = true
                isLoading.value = true
                loadWeatherInsight()   // fire-and-forget; the card self-hides until it resolves
                allZones.value = await detectionZoneStore.fetchDetectionZones()
                await detectionZoneTypeStore.fetchDetectionZoneTypes()
                const isEmpty = await openingHoursStore.fetchOpeningHours()
                if (isEmpty) {
                    openingHoursMode.value = 'all'
                    disabledOpeningHourModes.value = [
                        'only_regular_opening_hours',
                        'regular_and_irregular_opening_hours'
                    ]
                }
                
                // Set initial duration and load data
                changeDuration(30)
            })

            const loadData = async () => {
                // Load your data here based on the flowId
                try {
                    isLoading.value = true
                    const zone = allZones.value.find(z => z.DetectionZoneId === detectionZoneId.value)
                    
                    detectionZone.value = zone

                    let settings = zone.DetectionZoneSettings
                    if (typeof settings === 'string') {
                      try {
                        settings = JSON.parse(settings)
                      } catch (e) {
                        console.error("Could not parse DetectionZoneSettings:", e)
                        settings = {}
                      }
                    }

                    // extract SameIdInOutUniqueData flag
                    sameIdInOutSetting.value = settings.SameIdInOutUniqueData ?? false

                    // Service response — Product/Demo zones only. Fire-and-forget: the
                    // API resolves failures to null, and the panel renders nothing for
                    // a null, so a missing figure never blocks the rest of the page.
                    // Checkout throughput for a Checkout Interaction zone. Same
                    // endpoint the store view uses; the panel picks its own zone out.
                    checkoutTransactions.value = null
                    if (zone.DetectionZoneTypeId === 5) {
                        getCheckoutTransactions(serviceResponseSlot.value, openingHoursMode.value || '')
                            .then(r => { checkoutTransactions.value = (r && r.byZone) ? r : null })
                    }

                    serviceResponse.value = null
                    if ([6, 7].includes(zone.DetectionZoneTypeId)) {
                        getServiceResponse(zone.DetectionZoneId, serviceResponseSlot.value)
                            .then(r => { serviceResponse.value = r })
                    }

                    // Dwell Group opt-in (Mode C). Drives the "Estimated Avg
                    // Dwell" card visibility in the tripwire layout.
                    useTripwireForDwell.value = settings.UseTripwireForDwell === true

                    // Transition mode: derive the source zone's NAME from
                    // the composite key (flowId::zoneName::tripwireId) so
                    // the dwell card can show "Transit from <Zone>".
                    transitSourceZoneName.value = null
                    if (settings.DwellCalculationMode === 'transition'
                        && typeof settings.TransitionSourceKey === 'string') {
                        const parts = settings.TransitionSourceKey.split('::')
                        if (parts.length >= 2 && parts[1]) {
                            transitSourceZoneName.value = parts[1]
                        }
                    }

                    if (detectionZone.value) {
                        const flowId = zone.FlowId
                        const zoneName = zone.DetectionZoneName
                        detectionZoneTypeId.value = detectionZone.value.DetectionZoneTypeId
                        conversionRate.value = settings.EngagersConversionRate
                        productValue.value = settings.AverageProductValue

                        results.value = await getDetectionZoneData(dateRangeMode.value, duration.value, startDate.value, endDate.value, openingHoursMode.value, flowId, zoneName, detectionZoneTypeId.value)
                        console.log(results.value)
                        // Load total store visitors when we need it: for the
                        // Opportunity panel (type 6/7) AND for the tripwire
                        // "% of Total" stat surfaced in the tripwire layout.
                        const needsTotal = detectionZoneTypeId.value == 6
                            || detectionZoneTypeId.value == 7
                            || zoneHasTripwire.value
                        if (needsTotal) {
                          totalStoreVisitors.value = await getTotalStoreVisitors(dateRangeMode.value, duration.value, startDate.value, endDate.value, openingHoursMode.value, allZones.value)
                        }
                    } else {
                        console.warn('Detection Zone not found for ID:', detectionZoneId.value)
                    }
                } catch (error) {
                    console.error("Error loading data:", error)
                } finally {
                    isLoading.value = false
                }
            }

            // Watch for changes in Detection Zone ID and reload data if it changes
            watch(detectionZoneId, async () => {
              storeAnalytics.value = ""  
              await loadData()
            })

            const changeDuration = async (newDuration) => {
                duration.value = newDuration
                storeAnalytics.value = "" 
                // Same periods as before; any other value keeps the previous label.
                if ([1, 7, 30, 365, 1000, 2000, 3000, 4000].includes(Number(newDuration))) {
                    durationKey.value = Number(newDuration)
                }
                await loadData()
            }

            // Grounded zone reading summary — reads the nightly-precomputed summary
            // for this zone (instant; no enqueue/poll).
            const fetchZoneAnalytics = async () => {
              storeAnalytics.value = ""
              jobStatus.value = "pending"
              if (currentFetchController) currentFetchController.abort()
              const controller = new AbortController()
              currentFetchController = controller
              const requestId = ++latestRequestId
              try {
                if (!detectionZone.value) return
                const resp = await getAllenInsight({
                    level: 'zone',
                    zone: detectionZone.value.DetectionZoneName,
                    signal: controller.signal,
                })
                if (controller.signal.aborted || requestId !== latestRequestId) return
                if (resp.status === "complete") {
                    storeAnalytics.value = resp.result
                    jobStatus.value = "complete"
                } else if (resp.status === "unavailable") {
                    storeAnalytics.value = `<p>${t('zoneDetail.noInsights')}</p>`
                    jobStatus.value = "complete"
                } else if (resp.status === "error") {
                    jobStatus.value = "error"
                    console.error("Allen insight error:", resp.error)
                }
              }
              catch (err) {
                if (err.name !== 'AbortError') console.error("Unexpected error in fetchZoneAnalytics:", err)
              }
            }

            const changeDateRangeMode = async (newMode) => {
                dateRangeMode.value = newMode
            }

            const updateOpeningHoursMode = async (newMode) => { 
              openingHoursMode.value = newMode
              storeAnalytics.value = "" 
                await loadData()
            }

            const updateClearDatePicker = async (newValue) => {
                clearDatePicker.value = newValue
            }
            
            const updateStartDate = async (date) => {
                startDate.value = date
            }

            const updateEndDate = async (date) => {
                endDate.value = date
                storeAnalytics.value = "" 
                // only fetch when the user actually picked an end‐date in dateRangeMode
                if (date && dateRangeMode.value === 'dateRange') {
                  await loadData()
                }
            }

            const updateCountsGranularity = async (granularity) => {
                countsGranularity.value = granularity
            }

            const updateAvgDurGranularity = async (granularity) => {
                avgDurGranularity.value = granularity
            }

            const updateTripwireGranularity = async (granularity) => {
                tripwireGranularity.value = granularity
            }

            const toggleAllensInsights = async () => {
              showAllensInsights.value = !showAllensInsights.value

              if (showAllensInsights.value && storeAnalytics.value === "") {
                  await fetchZoneAnalytics()
              }
              else if (!showAllensInsights.value && currentFetchController) {
                  // hide panel → cancel any in-flight
                  currentFetchController.abort()
                  jobStatus.value = null 
              }
          }

            onUnmounted(() => {
                isMounted.value = false

                if (currentFetchController && !currentFetchController.signal.aborted) {
                    currentFetchController.abort()
                }
            })

            return {
                typeChange, NOTICES, ZONE_TYPE_NAMES, openingHoursChanged, thresholdChanged,
                pctText, numText, confidenceText, typeNameOf,
                zoneMarkers, countsMarkerCaption, avgDurMarkerCaption, tripwireMarkerCaption,
                serviceResponse,
                checkoutTransactions,
                detectionZoneStore,
                isLoading,
                // weather
                weatherEnabled,
                weatherZoneSignals,
                weatherDailyTemp,
                weatherOverlay,
                zoneWeatherCurve,
                storeAnalytics,
                showAllensInsights,
                toggleAllensInsights,
                detectionZone,
                detectionZoneTypeName,
                detectionZoneTypeId,
                sameIdInOutSetting,
                useTripwireForDwell,
                dwellEmptyStateText,
                hideDwellInAllMode,
                dwellAllModeNote,
                transitSourceZoneName,
                perWireBiasWarnings,
                isBurstyTraffic,
                hourlyBreakdown,
                isBimodalDwell,
                bimodalPeakSummary,
                mixtureBreakdown,
                isEntranceLikeZoneType,
                dwellPanelTitle,
                formatDwellSeconds,
                dwellConfidenceTooltip,
                dwellMethodTooltip,
                zoneColor,
                zoneHasPolygon,
                zoneHasTripwire,
                zoneGeometryLabel,
                results,
                checkoutQueue,
                checkoutQueueVerdict,
                formattedTopVisitorDays,
                formattedTopVisitorHours,
                formattedBusiestAvgHours,
                useTripwireMode,
                useCrossingsLabel,
                crossingsPanelTitle,
                trendsPanelTitle,
                topHighlightsTitle,
                avgComparisonTitle,
                effectiveHeatmapData,
                effectiveTrendTotal,
                effectiveAvgPerDay,
                effectiveAvgSameWeekday,
                effectiveMaxHourCount,
                effectiveMaxDayCount,
                effectiveMaxAvgPerHour,
                effectiveFormattedTopVisitorDays,
                effectiveFormattedTopVisitorHours,
                effectiveFormattedBusiestAvgHours,
                tripwirePctOfTotal,
                changeDuration,
                changeDateRangeMode,
                updateOpeningHoursMode,
                openingHoursMode,
                disabledOpeningHourModes,
                updateClearDatePicker,
                clearDatePicker,
                updateStartDate,
                updateEndDate,
                updateCountsGranularity,
                countsGranularity,
                countsChartData,
                updateAvgDurGranularity,
                avgDurGranularity,
                avgDurChartData,
                updateTripwireGranularity,
                tripwireGranularity,
                tripwireChartData,
                hasTripwireActivity,
                tripwireInLabel,
                tripwireOutLabel,
                totalStoreVisitors,
                conversionRate,
                productValue,
                startDate,
                endDate,
                duration,
                durationText,
                dateRangeMode,
            }
        
        }

    }
</script>

<style scoped>
/* 6-col grid so two x 3-span = side-by-side; 6-span = full width */
.row2-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}

.row2-item { min-width: 0; }       /* allow charts to shrink nicely */
.row2-span-3 { grid-column: span 3; }
.row2-span-6 { grid-column: span 6; }

.zone-row4 {
  /* ChartGrid already sets display:grid; we only override columns/gap */
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Small note under header */
.same-id-note{
  color:#C24500; font-size:.9rem; padding-left:.5rem; margin:.4rem 0 .2rem;
}

/* Checkout queue card — line length in a checkout's waiting zone */
.checkout-queue-card{
  margin: 14px 0 2px;
  padding: 12px 14px;
  background: #fbf1f0;
  border: 1px solid #f0d9d7;
  border-left: 4px solid #AD3935;
  border-radius: 12px;
}
.checkout-queue-card .cq-head{
  display: inline-flex; align-items: center; gap: 7px;
  font-size: .8rem; font-weight: 700; text-transform: uppercase; letter-spacing: .5px;
  color: #AD3935; margin-bottom: 5px;
}
.checkout-queue-card .cq-body{
  margin: 0; font-size: .92rem; line-height: 1.5; color: #6b5250;
}
.checkout-queue-card .cq-body .cq-verdict{ color: #8f2b23; font-weight: 700; }

/* Inline toolbar area atop a panel (for the granularity radios) */
.grid-panel-toolbar{
  display: flex; align-items: center; justify-content: center; gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.wx-overlay-toggle{
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.78rem; color: #178f8f; cursor: pointer;
  padding: 3px 9px; border: 1px solid #d9ece9; border-radius: 999px;
  background: #f3faf9; user-select: none; margin-top: 6px;
}
.wx-overlay-toggle input{ accent-color: #1f9d9d; cursor: pointer; }
.wx-overlay-toggle .bi{ font-size: 0.85rem; }

/* Tripwire-only zone layout — 6-column grid so each row can split independently.

   Daily granularity:
     Row 1: Crossings (4/6 = 2/3) | Top Highlights (2/6 = 1/3)
     Row 2: Heatmap   (3/6 = 1/2) | Trends + Avg Comparison stacked (3/6 = 1/2)

   Hourly granularity (Crossings line gets the full width):
     Row 1: Crossings (full)
     Row 2: Highlights (3/6) | Heatmap (3/6)
     Row 3: Trends + Avg Comparison side-by-side (full row, flex row)

   align-items: start lets the heatmap be a bit taller than the right-column
   stack without stretching the row (no whitespace forced into the stack). */
.tripwire-layout-grid{
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: auto auto;
  grid-template-areas:
    "crossings crossings crossings crossings highlights   highlights"
    "heatmap   heatmap   heatmap   trends-stk trends-stk  trends-stk";
  gap: 16px;
  align-items: start;
}
.tw-area-crossings    { grid-area: crossings; }
.tw-area-highlights   { grid-area: highlights; }
.tw-area-heatmap      { grid-area: heatmap; }
.tw-area-trends-stack {
  grid-area: trends-stk;
  display: flex; flex-direction: column; gap: 16px;
}

/* Force the Top Highlights subcards into a single stacked column when used
   inside the narrow tripwire-mode column (the component's own auto-fit grid
   would otherwise re-multi-column at wider screens). */
.tw-area-highlights :deep(.mini-cards){
  grid-template-columns: 1fr;
}

.tripwire-layout-grid.tripwire-hourly{
  grid-template-rows: auto auto auto;
  grid-template-areas:
    "crossings  crossings  crossings  crossings  crossings  crossings"
    "highlights highlights highlights heatmap    heatmap    heatmap"
    "trends-stk trends-stk trends-stk trends-stk trends-stk trends-stk";
}
/* Hourly: stack flips to a row so trends and avg sit side-by-side. */
.tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack{
  flex-direction: row;
}
.tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack > *{
  flex: 1;
}

/* 1250 (not 1024): below this the 6-col layout squeezes the crossings panel so
   its toolbar (totals + daily/hourly toggle) wraps and eats the chart's legend
   + x-axis (~1130), and the highlights / heatmap-side cards get too narrow
   (~1020). Stacking everything full-width sooner avoids all of that. */
@media (max-width: 1250px){
  .tripwire-layout-grid,
  .tripwire-layout-grid.tripwire-hourly{
    grid-template-columns: 1fr;
    grid-template-areas:
      "crossings"
      "highlights"
      "heatmap"
      "trends-stk";
  }
  .tripwire-layout-grid.tripwire-hourly .tw-area-trends-stack{
    flex-direction: column;
  }
}

/* Compact override — tighter inner padding for the tripwire mode panels so
   the dense grid doesn't waste pixels on chrome. */
.tw-compact :deep(.panel-body){
  padding: 8px 10px;
}
.tw-compact :deep(.panel-head){
  padding: 6px 10px;
}

/* "% of Total" stat display inside its compact panel */
.pct-of-total{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 4px;
}
.pct-of-total .pct-value{
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1;
}
.pct-of-total .pct-context{
  font-size: 0.85rem;
  color: #6b7a86;
  text-align: center;
}

/* Estimated Avg Dwell (Mode C — Little's Law) */
.dwell-estimate{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 6px;
  text-align: center;
}
.dwell-value-row{
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
.dwell-value{
  font-size: 2.1rem;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.dwell-conf{
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
}
/* The backend only emits 'high' or 'medium' — anything weaker is gated as
   "no estimate" and the panel falls through to its empty-state copy. */
.dwell-conf.conf-high{   color: #2f6f3a; background: #f4faf5; border: 1px solid #bfe2c7; }
.dwell-conf.conf-medium{ color: #9a6b00; background: #fff8e6; border: 1px solid #ffd97a; }
.dwell-context{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .78rem;
  color: #6b7a86;
}
.dwell-context i{ opacity: .7; }
.hourly-chip-row{
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.hourly-chip{
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: .72rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f3f5f7;
  color: #4a5764;
  border: 1px solid #e3e8ed;
}
.hourly-chip strong{ color: #2a3540; font-weight: 600; }
.hourly-chip-peak{
  background: #fff1de;
  border-color: #ffce80;
  color: #8a5400;
}
.hourly-chip-peak strong{ color: #6b3f00; }
.grid-panel-toolbar.tripwire-toolbar{
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.tripwire-totals{
  display: flex; gap: 10px; flex-wrap: wrap;
}
.tw-pill{
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  font-size: .85rem; color: #2b3a44;
  border: 1px solid #e6edf3; background: #f9fbfd;
}
.tw-pill i{ opacity: .8 }
.tw-pill.tw-in{ color: #4F7EB3 }
.tw-pill.tw-out{ color: #6C9046 }
.tw-pill b{ color: #2b3a44; font-weight: 600 }

/* Only affect this Trends panel */
.panel-trends :deep(.panel-body){
  height: auto;           /* override var(--panel-h) result */
  min-height: 120px;      /* keeps a nice base height at wide screens */
}

.panel-top-visitors:deep(.panel-body) {
  height: auto;            /* override fixed height */
  min-height: 120px;       /* keep a tidy base height */
}

/* Let content define its own height when parent is height:auto */
.panel-trends :deep(.panel-slot){
  height: auto;
}

.chart-vert-stack{
  display: grid;
  grid-auto-rows: minmax(0, auto);
  gap: 12px;         /* match ChartGrid gap */
}

/* Empty state text for panels where strategy is insufficient */
.empty-fallback{
  margin-top: 140px; text-align: center; color: #425461; font-style: italic;
}

/* Geometry chip in the section header */
.zone-geom-chip{
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:999px;
  background:#f4f7fb; border:1px solid #e6edf3; color:#425461;
  font-size:.82rem; font-weight:600;
}
.zone-geom-chip i{ color:#4F7EB3 }
.zone-geom-text{ font-weight:700 }

/* Wrapper */
/* Width is intentionally not set to 100% so the explicit left/right margins
   on the wrapping div actually inset from the page edges (matching the
   store-level Data tab layout). */
.dashboard-wrapper { overflow-x:hidden; padding-bottom: 40px;}

/* Loader overlay (keep) */
.loader-overlay {
  position: absolute; top:0; left:0; width:100%; height:100%;
  display:flex; align-items:center; justify-content:center;
  background-color: rgba(255,255,255,0.8); z-index: 1000;
}
.loader { width:160px; margin-top:0; padding:2px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m:conic-gradient(#0000 10%,#000),linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation: spinner 1s infinite linear;
}
@keyframes spinner { to { transform: rotate(1turn) } }

/* Modal (keep) */
.modal-overlay {
  position: fixed; inset:0; background: rgba(0,0,0,0.5);
  display:flex; align-items:center; justify-content:center; z-index:2100; /* above nav sidebar (1100) */
}
.modal-window {
  /* centered over the full viewport — no sidebar offset; fluid min-width */
  background:white; border-radius:8px; min-width:min(400px, 92vw); max-width:92vw; max-height:90vh;
  overflow:auto; padding:1rem; position:relative; box-shadow:0 4px 12px rgba(0,0,0,0.3);
}

/* 3-up grid for the first row */
.chart-grid--3 { display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 12px; }
@media (max-width: 1520px){ 
  .chart-grid--3 { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .row2-grid { grid-template-columns: 1fr; }
  .row2-span-3, .row2-span-6 { grid-column: 1 / -1; }
  .zone-row4 {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 1250px){ .chart-grid--3 { grid-template-columns: 1fr; } }

/* 2-up inner grid for the split “per day” panels (collapses nicely) */
.chart-grid--2 { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 12px; }
@media (max-width: 1250px){ .chart-grid--2 { grid-template-columns: 1fr; } }

</style>